// Admin overlay (Joe only). Add-an-address, a status note, and one row per
// household. Each row opens a detail panel: "view" shows the household's answer
// (including the admin-only budget); "enter for them" opens the real form to
// fill on their behalf. Gated on isAdmin (a custom claim + Firestore rules).
import type { CSSProperties } from 'react';
import type { RosterHousehold } from '../services';
import type { PlanWindow, ReunionForm, TravelMode, WeekOption } from '../types';
import { MONO } from './primitives';
import { ResponseForm } from './respond/ResponseForm';

export interface AdminDetail {
  hid: string;
  display: string;
  mode: 'view' | 'enter';
}

const FIELD_LABEL: CSSProperties = {
  fontFamily: MONO,
  fontSize: 12,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 4,
};
const FIELD_VALUE: CSSProperties = { fontSize: 19, lineHeight: 1.5, color: 'var(--bone)' };

function Field({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <div style={FIELD_LABEL}>{label}</div>
      <div style={{ ...FIELD_VALUE, color: muted ? 'var(--muted-2)' : FIELD_VALUE.color }}>{value}</div>
    </div>
  );
}

function ResponseView({
  form,
  weeks,
  travelModes,
}: {
  form: ReunionForm;
  weeks: WeekOption[];
  travelModes: TravelMode[];
}) {
  const ruledOut = weeks.filter((w) => form.weeks.includes(w.id)).map((w) => w.label);
  const travel = travelModes.find((m) => m.id === form.travel)?.label;
  const heads = (Number(form.adults) || 0) + (Number(form.kids) || 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="on-card" style={CARD}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label="weeks ruled out"
            value={ruledOut.length ? ruledOut.join(' · ') : 'none — any of them works'}
            muted={!ruledOut.length}
          />
          <Field
            label="who's coming"
            value={`${form.adults || '0'} adults · ${form.kids || '0'} kids${heads ? ` · ${heads} total` : ''}`}
          />
          {form.ages.trim() && <Field label="kids' ages" value={form.ages} />}
          <Field label="drive or fly" value={travel ?? '—'} muted={!travel} />
          {form.notes.trim() && <Field label="anything else" value={form.notes} />}
        </div>
      </div>

      {/* the private budget — admin can read it; keep the "only i see this" treatment */}
      <div
        className="on-card-alt"
        style={{
          background: 'var(--card-alt)',
          border: '2px dashed rgba(127,227,232,0.55)',
          borderRadius: 16,
          padding: '22px 20px',
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--cyan)',
            marginBottom: 10,
          }}
        >
          the private number · only you see this
        </div>
        <div style={{ ...FIELD_VALUE, color: form.budget.trim() ? 'var(--bone)' : 'var(--muted-2)' }}>
          {form.budget.trim() || 'they haven’t said'}
        </div>
      </div>
    </div>
  );
}

const CARD: CSSProperties = {
  background: 'var(--deeper)',
  border: '1px solid rgba(127,227,232,0.24)',
  borderRadius: 16,
  padding: '22px 20px',
};

export function AdminOverlay({
  rows,
  note,
  newEmail,
  onNewEmail,
  onAdd,
  onClose,
  detail,
  form,
  setForm,
  weeks,
  window,
  travelModes,
  busy,
  onOpenDetail,
  onCloseDetail,
  onEditDetail,
  onSubmitDetail,
}: {
  rows: RosterHousehold[];
  note: string;
  newEmail: string;
  onNewEmail: (v: string) => void;
  onAdd: () => void;
  onClose: () => void;
  detail: AdminDetail | null;
  form: ReunionForm;
  setForm: (p: Partial<ReunionForm>) => void;
  weeks: WeekOption[];
  window: PlanWindow | null;
  travelModes: TravelMode[];
  busy: boolean;
  onOpenDetail: (hid: string, display: string, responded: boolean) => void;
  onCloseDetail: () => void;
  onEditDetail: () => void;
  onSubmitDetail: () => void;
}) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'var(--deep)', overflow: 'auto' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 60px' }}>
        {detail ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
              <div>
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: 'var(--orange)',
                  }}
                >
                  {detail.mode === 'view' ? 'their answer' : 'entering for them'}
                </div>
                <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.04em', marginTop: 6 }}>
                  {detail.display}
                </div>
              </div>
              <button
                type="button"
                className="wh-ghost-neutral on-deep"
                onClick={onCloseDetail}
                style={{ minHeight: 52, padding: '0 18px', fontSize: 17, color: 'var(--bone)' }}
              >
                ← the list
              </button>
            </div>

            <div style={{ marginTop: 22 }}>
              {busy ? (
                <div style={{ fontFamily: MONO, fontSize: 14, color: 'var(--muted)' }}>loading…</div>
              ) : detail.mode === 'view' ? (
                <>
                  <ResponseView form={form} weeks={weeks} travelModes={travelModes} />
                  <button
                    type="button"
                    className="wh-ghost on-deep"
                    onClick={onEditDetail}
                    style={{ minHeight: 56, padding: '0 22px', fontSize: 18, fontWeight: 600, marginTop: 18 }}
                  >
                    edit these answers
                  </button>
                </>
              ) : (
                <ResponseForm
                  householdLabel={detail.display}
                  banner={
                    <>
                      entering for <strong>{detail.display}</strong> — save what they told you. they can
                      still sign in and change it.
                    </>
                  }
                  submitLabel={`save ${detail.display}'s answers`}
                  form={form}
                  setForm={setForm}
                  weeks={weeks}
                  window={window}
                  travelModes={travelModes}
                  onSubmit={onSubmitDetail}
                  busy={busy}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
              <div>
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: 'var(--orange)',
                  }}
                >
                  admin · me only
                </div>
                <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.04em', marginTop: 6 }}>the list</div>
              </div>
              <button
                type="button"
                className="wh-ghost-neutral on-deep"
                onClick={onClose}
                style={{ minHeight: 52, padding: '0 18px', fontSize: 17, color: 'var(--bone)' }}
              >
                close
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <input
                className="wh-input on-deep"
                type="email"
                placeholder="add an address"
                value={newEmail}
                onChange={(e) => onNewEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAdd();
                }}
                style={{ flex: 1, minWidth: 0, minHeight: 58, padding: '12px 16px', fontSize: 18, background: 'var(--deeper)' }}
              />
              <button
                type="button"
                className="wh-solid on-deep"
                onClick={onAdd}
                style={{ minHeight: 58, padding: '0 20px', fontSize: 18 }}
              >
                add
              </button>
            </div>
            <div style={{ fontFamily: MONO, fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>{note}</div>

            {(() => {
              const answered = rows.filter((r) => r.responded).length;
              const signedIn = rows.filter((r) => !r.responded && r.signedInAt).length;
              const codeSent = rows.filter((r) => !r.responded && !r.signedInAt && r.codeSentAt).length;
              const waiting = rows.length - answered - signedIn - codeSent;
              return (
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: 13,
                    color: 'var(--cyan)',
                    marginTop: 16,
                    lineHeight: 1.7,
                  }}
                >
                  {answered} answered · {signedIn} signed in · {codeSent} code sent ·{' '}
                  <span style={{ color: 'var(--muted-2)' }}>{waiting} waiting</span>
                </div>
              );
            })()}

            <div style={{ marginTop: 20, borderTop: '1px solid rgba(234,247,248,0.16)' }}>
              {rows.map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(234,247,248,0.12)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>{row.display}</div>
                    <div style={{ fontFamily: MONO, fontSize: 13, color: 'var(--muted-2)', wordBreak: 'break-all' }}>
                      {row.emails} · gen {row.generation}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: MONO,
                      fontSize: 13,
                      color: row.responded
                        ? 'var(--cyan)'
                        : row.signedInAt
                          ? 'var(--cyan-soft)'
                          : row.codeSentAt
                            ? 'var(--muted)'
                            : 'var(--muted-2)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.responded
                      ? 'answered'
                      : row.signedInAt
                        ? 'signed in'
                        : row.codeSentAt
                          ? 'code sent'
                          : 'waiting'}
                  </div>
                  <button
                    type="button"
                    className="wh-ghost on-deep"
                    onClick={() => onOpenDetail(row.id, row.display, row.responded)}
                    style={{
                      minHeight: 48,
                      padding: '0 14px',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                      borderColor: 'rgba(127,227,232,0.4)',
                    }}
                  >
                    {row.responded ? 'view' : 'enter for them'}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
